import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError, avatarUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { SectionTitle, Card, StyleTag } from "../components/ui-bits";
import { Avatar } from "../components/Avatar";
import { STYLES } from "../lib/constants";
import { Camera, Loader2, Save } from "lucide-react";
import { toast } from "sonner";

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const [form, setForm] = useState({ pseudo: "", artist_name: "", description: "", styles: [], socials: {} });
  const [busy, setBusy] = useState(false);
  const [prodCount, setProdCount] = useState(0);
  const photoInput = useRef();

  useEffect(() => {
    api.get("/profiles/me").then((r) => {
      const u = r.data;
      setForm({ pseudo: u.pseudo || "", artist_name: u.artist_name || "", description: u.description || "",
        styles: u.styles || [], socials: u.socials || {} });
      setProdCount(u.production_count || 0);
    });
  }, []);

  const toggleStyle = (s) => setForm((f) => ({ ...f, styles: f.styles.includes(s) ? f.styles.filter((x) => x !== s) : [...f.styles, s] }));

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/profiles/me", form);
      await refreshUser();
      toast.success("Profil mis à jour");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post("/profiles/me/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refreshUser();
      toast.success("Photo mise à jour");
    } catch { toast.error("Erreur upload photo"); }
  };

  const isBeatmaker = user?.role === "beatmaker";

  return (
    <div className="max-w-2xl mx-auto">
      <SectionTitle>Mon profil</SectionTitle>
      <Card className="p-6">
        <div className="flex items-center gap-4 mb-6">
          <div className="relative">
            <Avatar user={user} size={72} />
            <button onClick={() => photoInput.current.click()} data-testid="upload-photo-button"
              className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-violet-600 flex items-center justify-center border-2 border-[#0B0716]">
              <Camera size={14} />
            </button>
            <input ref={photoInput} type="file" accept="image/*" className="hidden" onChange={uploadPhoto} />
          </div>
          <div>
            <p className="font-display font-bold text-lg">@{form.pseudo}</p>
            <p className="text-sm text-zinc-400 capitalize">{user?.role}</p>
            {isBeatmaker && <p className="text-xs font-mono text-violet-300 mt-0.5">{prodCount} production(s)</p>}
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-zinc-300">Pseudo</label>
            <input data-testid="profile-pseudo" value={form.pseudo} onChange={(e) => setForm({ ...form, pseudo: e.target.value })}
              className="mt-1.5 w-full bg-zinc-900 border border-zinc-700 rounded-xl px-4 py-3 text-sm focus:border-violet-500 outline-none" />
          </div>
          {!isBeatmaker && (
            <div>
              <label className="text-sm font-medium text-zinc-300">Nom d'artiste</label>
              <input data-testid="profile-artistname" value={form.artist_name} onChange={(e) => setForm({ ...form, artist_name: e.target.value })}
                className="mt-1.5 w-full bg-zinc-900 border border-zinc-700 rounded-xl px-4 py-3 text-sm focus:border-violet-500 outline-none" />
            </div>
          )}
          <div>
            <label className="text-sm font-medium text-zinc-300">Description</label>
            <textarea data-testid="profile-description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="mt-1.5 w-full bg-zinc-900 border border-zinc-700 rounded-xl px-4 py-3 text-sm focus:border-violet-500 outline-none resize-none" />
          </div>
          <div>
            <label className="text-sm font-medium text-zinc-300">Styles musicaux</label>
            <div className="flex flex-wrap gap-2 mt-2" data-testid="profile-styles">
              {STYLES.map((s) => <StyleTag key={s} active={form.styles.includes(s)} onClick={() => toggleStyle(s)}>{s}</StyleTag>)}
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-zinc-300">Instagram</label>
            <input data-testid="profile-instagram" value={form.socials?.instagram || ""} onChange={(e) => setForm({ ...form, socials: { ...form.socials, instagram: e.target.value } })}
              placeholder="@ton_compte" className="mt-1.5 w-full bg-zinc-900 border border-zinc-700 rounded-xl px-4 py-3 text-sm focus:border-violet-500 outline-none" />
          </div>
          <button onClick={save} disabled={busy} data-testid="save-profile-button"
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 font-semibold transition-all disabled:opacity-60">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Enregistrer
          </button>
        </div>
      </Card>
    </div>
  );
}
